/**
 * "Add {name} to the advertiser registry" — shown when a quote comes back
 * `needsAdvertiser` (unknown name) or `ambiguous` (several registry matches).
 * The category is preselected from the Haiku suggestion; the human confirms
 * it, so the record is saved as confirmed/manual. Conflicts from the server
 * (exact duplicate, loose matches) are surfaced with "Use {name}" shortcuts.
 *
 * The Creators dashboard's Brand logos tab reuses it for report names that
 * aren't in the registry, and for brands on no report yet, through the
 * optional props (intro, submitLabel, eyebrow, editableName, keepOpenedName,
 * suggestion). Without them it is the Sales dialog, copy unchanged.
 */

import React, { useId, useMemo, useState } from 'react';
import { Button, Modal, StatusPill } from '../../ui';
import ChipInput from './ChipInput';
import { AdvertiserConflictError, createAdvertiser } from '../../../services/mediaPlansApi';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, NeedsAdvertiser } from './types';
import { CONFIDENCE_LABELS, CONFIDENCE_VARIANT, DIRECT_AGENCY_LABEL, agencyOptions } from './vocab';
import { bannerStyle, fieldStyle, gridStyle, hintStyle, labelStyle, mutedText } from './formStyles';

export type AdvertiserDialogState =
  | { kind: 'needsAdvertiser'; needs: NeedsAdvertiser }
  | { kind: 'ambiguous'; name: string; error: string; candidates: Array<{ key: string; name: string }> };

/** The optional variant props; every one defaults to the Sales dialog. */
interface VariantProps {
  /** Replaces the opening paragraph (the Sales copy is about pricing the plan). */
  intro?: React.ReactNode;
  /** Replaces "Add advertiser & get quote". */
  submitLabel?: string;
  /** Replaces the "New to our network" eyebrow. */
  eyebrow?: string;
  /**
   * Shows an editable Name field, starting from the canonical name. When the
   * saved name differs from the name the dialog opened for (`needs.name`),
   * that name is always saved as an alias too, so it keeps matching.
   */
  editableName?: boolean;
  /**
   * With editableName: false saves only the names the form shows — for a name
   * someone typed (Brand logos → + Add brand), which matches nothing yet, so a
   * typo must not become an alias. Defaults to true (a report name keeps matching).
   */
  keepOpenedName?: boolean;
  /** An existing record the name probably is, offered as "Use {name}" before anything is created. */
  suggestion?: { key: string; name: string; reason?: string };
}

interface Props extends VariantProps {
  state: AdvertiserDialogState | null;
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  /** Preselect the agency the seller already chose on the inputs screen. */
  defaultAgencyKey?: string;
  onCreated: (advertiser: AdvertiserRecord) => void;
  onUseExisting: (key: string, name: string) => void;
  onCancel: () => void;
}

interface FormProps extends VariantProps {
  needs: NeedsAdvertiser;
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  defaultAgencyKey?: string;
  onCreated: (advertiser: AdvertiserRecord) => void;
  onUseExisting: (key: string, name: string) => void;
  onCancel: () => void;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const AddAdvertiserForm: React.FC<FormProps> = ({
  needs,
  categories,
  agencies,
  defaultAgencyKey,
  onCreated,
  onUseExisting,
  onCancel,
  intro,
  submitLabel,
  eyebrow,
  editableName,
  keepOpenedName = true,
  suggestion,
}) => {
  const id = useId();
  const [name, setName] = useState(needs.canonicalName);
  const [categoryKey, setCategoryKey] = useState(needs.suggestion.categoryKey);
  const [agencyKey, setAgencyKey] = useState(defaultAgencyKey && defaultAgencyKey !== '__other__' ? defaultAgencyKey : '');
  const [parentCompany, setParentCompany] = useState('');
  const [aliases, setAliases] = useState<string[]>(() => (needs.name.trim() !== needs.canonicalName.trim() ? [needs.name.trim()] : []));
  const [gamIds, setGamIds] = useState<string[]>(() => needs.gamCandidates.map((c) => c.id));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<AdvertiserConflictError | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categoryOptions = useMemo(() => {
    const fromRef = categories.length ? categories : needs.categories.map((c) => ({ key: c.key, label: c.label }));
    return [...fromRef].sort((a, b) => a.label.localeCompare(b.label));
  }, [categories, needs]);
  const selectedCategory = categoryOptions.find((c) => c.key === categoryKey) as MediaPlanCategory | undefined;

  const savedName = editableName ? name.trim() : needs.canonicalName;

  const submit = async (force: boolean) => {
    if (!savedName) {
      setError('Enter the advertiser’s name.');
      return;
    }
    if (!categoryKey) {
      setError('Choose an industry category.');
      return;
    }
    // A renamed record must still match the name it was opened for.
    const openedFor = needs.name.trim();
    const addOpenedName =
      editableName && keepOpenedName && openedFor && !sameName(savedName, openedFor) && !aliases.some((a) => sameName(a, openedFor));
    setSaving(true);
    setError(null);
    try {
      const record = await createAdvertiser({
        name: savedName,
        categoryKey,
        aliases: addOpenedName ? [...aliases, openedFor] : aliases,
        gamAdvertiserIds: gamIds,
        agencyKey: agencyKey || undefined,
        parentCompany: parentCompany.trim() || undefined,
        notes: notes.trim() || undefined,
        force,
      });
      onCreated(record);
    } catch (err) {
      if (err instanceof AdvertiserConflictError) setConflict(err);
      else setError(err instanceof Error ? err.message : 'Could not add the advertiser.');
    } finally {
      setSaving(false);
    }
  };

  const n = needs;
  return (
    <Modal
      open
      onClose={onCancel}
      eyebrow={eyebrow ?? 'New to our network'}
      title={`Add ${editableName ? savedName || n.name : n.canonicalName} to the advertiser registry`}
      width={640}
      closeOnEsc={false}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {conflict?.kind === 'possible_duplicates' ? (
            <Button variant="primary" onClick={() => submit(true)} disabled={saving}>
              {saving ? 'Adding…' : 'Create anyway'}
            </Button>
          ) : conflict?.kind === 'already_exists' ? null : (
            <Button variant="primary" onClick={() => submit(false)} disabled={saving}>
              {saving ? 'Adding…' : submitLabel ?? 'Add advertiser & get quote'}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ ...mutedText, margin: 0 }}>
          {intro ?? <>Nothing in the registry matches “{n.name}”. Confirm the industry category — it picks any category rate card and the category average shown with the rates — and we'll save the advertiser and price the plan.</>}
        </p>

        {suggestion && !conflict && (
          <div style={bannerStyle('info')}>
            <div style={{ flex: 1 }}>
              <div>Is it {suggestion.name}?{suggestion.reason ? ` (${suggestion.reason})` : ''}</div>
              <div style={styles.candList}>
                <div style={styles.candRow}>
                  <span style={{ fontWeight: 600 }}>{suggestion.name}</span>
                  <Button size="sm" variant="subtle" onClick={() => onUseExisting(suggestion.key, suggestion.name)} disabled={saving}>
                    Use {suggestion.name}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {editableName && (
          <div>
            <label htmlFor={`${id}-name`} style={labelStyle}>Name</label>
            <input id={`${id}-name`} style={fieldStyle} value={name} maxLength={160} disabled={saving} onChange={(e) => setName(e.target.value)} />
            <div style={hintStyle}>
              The advertiser’s name in the registry.{' '}
              {keepOpenedName && !sameName(name, n.name) && <>“{n.name}” is saved as an alias, so it keeps matching.</>}
            </div>
          </div>
        )}

        {conflict && (
          <div role="alert" style={bannerStyle(conflict.kind === 'already_exists' ? 'info' : 'warning')}>
            <div style={{ flex: 1 }}>
              <div>{conflict.kind === 'already_exists' ? 'Already in the registry:' : 'Did you mean one of these?'}</div>
              <div style={styles.candList}>
                {conflict.candidates.map((c) => (
                  <div key={c.key} style={styles.candRow}>
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    <Button size="sm" variant="subtle" onClick={() => onUseExisting(c.key, c.name)}>
                      Use {c.name}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor={`${id}-cat`} style={labelStyle}>Industry category</label>
          {/* Wraps on a phone, where the pill would otherwise squeeze the select to its arrow. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select id={`${id}-cat`} style={{ ...fieldStyle, cursor: 'pointer', flex: '1 1 180px' }} value={categoryKey} disabled={saving} onChange={(e) => setCategoryKey(e.target.value)}>
              <option value="">Choose a category…</option>
              {categoryOptions.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
            {categoryKey === n.suggestion.categoryKey && (
              <StatusPill variant={CONFIDENCE_VARIANT[n.suggestion.confidence]} size="sm">
                Suggested · {CONFIDENCE_LABELS[n.suggestion.confidence]}
              </StatusPill>
            )}
          </div>
          <div style={hintStyle}>
            {categoryKey === n.suggestion.categoryKey ? n.suggestion.rationale : selectedCategory?.description || 'Pick the category the advertiser’s own business falls in, not the agency’s.'}
          </div>
        </div>

        <div style={gridStyle}>
          <div>
            <label htmlFor={`${id}-agency`} style={labelStyle}>Agency <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <select id={`${id}-agency`} style={{ ...fieldStyle, cursor: 'pointer' }} value={agencyKey} disabled={saving} onChange={(e) => setAgencyKey(e.target.value)}>
              <option value="">{DIRECT_AGENCY_LABEL}</option>
              {agencyOptions(agencies).map((a) => (
                <option key={a.key} value={a.key}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${id}-parent`} style={labelStyle}>Parent company <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <input id={`${id}-parent`} style={fieldStyle} value={parentCompany} maxLength={160} disabled={saving} onChange={(e) => setParentCompany(e.target.value)} />
          </div>
        </div>

        <ChipInput label="Also known as" values={aliases} onChange={setAliases} placeholder="Other names this advertiser books under…" hint="Aliases let future plans match this advertiser however the buyer writes the name." disabled={saving} />

        {n.gamCandidates.length > 0 && (
          <div>
            <div style={labelStyle}>Google Ad Manager advertisers</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {n.gamCandidates.map((c) => (
                <label key={c.id} style={styles.check}>
                  <input
                    type="checkbox"
                    checked={gamIds.includes(c.id)}
                    disabled={saving}
                    onChange={(e) => setGamIds(e.target.checked ? [...gamIds, c.id] : gamIds.filter((x) => x !== c.id))}
                  />
                  {c.name} <span style={mutedText}>· {c.id}</span>
                </label>
              ))}
            </div>
            <div style={hintStyle}>Linking GAM ids lets delivery history flow into this advertiser's pricing history.</div>
          </div>
        )}

        <div>
          <label htmlFor={`${id}-notes`} style={labelStyle}>Notes <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(internal)</span></label>
          <textarea id={`${id}-notes`} style={{ ...fieldStyle, minHeight: 56, resize: 'vertical', fontFamily: 'var(--font-sans)' }} value={notes} maxLength={1000} disabled={saving} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {error && <div role="alert" style={bannerStyle('danger')}>{error}</div>}
      </div>
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  candList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 },
  candRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '6px 10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface-2)' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fg-2)', cursor: 'pointer' },
};

const NewAdvertiserDialog: React.FC<Props> = ({ state, categories, agencies, defaultAgencyKey, onCreated, onUseExisting, onCancel, ...variant }) => {
  if (!state) return null;
  if (state.kind === 'ambiguous') {
    return (
      <Modal open onClose={onCancel} title="Which advertiser did you mean?" width={520} closeOnEsc={false} footer={<Button variant="ghost" onClick={onCancel}>Cancel</Button>}>
        <p style={{ ...mutedText, marginTop: 0 }}>“{state.name}” matches several advertisers in the registry.</p>
        <div style={styles.candList}>
          {state.candidates.map((c) => (
            <div key={c.key} style={styles.candRow}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              <Button size="sm" variant="subtle" onClick={() => onUseExisting(c.key, c.name)}>
                Use {c.name}
              </Button>
            </div>
          ))}
        </div>
      </Modal>
    );
  }
  return (
    <AddAdvertiserForm
      key={state.needs.proposedKey || state.needs.canonicalName}
      needs={state.needs}
      categories={categories}
      agencies={agencies}
      defaultAgencyKey={defaultAgencyKey}
      onCreated={onCreated}
      onUseExisting={onUseExisting}
      onCancel={onCancel}
      {...variant}
    />
  );
};

export default NewAdvertiserDialog;
